import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, Overline } from "./motion";
import { useLang, useT } from "@/i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldCls =
  "w-full rounded-none border-0 border-b border-ink/40 bg-transparent px-0 py-3 text-base font-light text-ink placeholder:text-ink/70 focus:border-ink focus:outline-none transition-colors duration-300";

const Field = ({ label, testId, required, warning, ...props }) => (
  <label className="block">
    <span className="text-xs font-medium uppercase tracking-[0.2em] text-ink/75">
      {label}
      {required && (
        <span aria-hidden="true" className="text-ink">
          {" "}
          *
        </span>
      )}
    </span>
    {props.as === "textarea" ? (
      <textarea
        data-testid={testId}
        rows={4}
        required={required}
        className={`${fieldCls} resize-none`}
        {...props}
      />
    ) : (
      <input data-testid={testId} required={required} className={fieldCls} {...props} />
    )}
    {warning && (
      <span data-testid={`${testId}-warning`} className="mt-2 block text-xs leading-relaxed text-ink/75">
        {warning}
      </span>
    )}
  </label>
);

export default function ContactSection() {
  const t = useT();
  const lang = useLang();
  const [mode, setMode] = useState("enquiry");
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    preferred_time: "",
    website: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);
  const tabRefs = useRef([]);

  const TABS = [
    { id: "enquiry", label: t.contact.tabEnquiry, short: t.contact.tabEnquiry },
    { id: "call", label: t.contact.tabCall, short: t.contact.tabCallShort },
  ];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const selectTab = (id) => {
    setMode(id);
    setDone(false);
    setError(false);
  };

  const onTabKeyDown = (e, index) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const next =
      e.key === "ArrowRight"
        ? (index + 1) % TABS.length
        : (index - 1 + TABS.length) % TABS.length;
    selectTab(TABS[next].id);
    tabRefs.current[next]?.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(false);
    try {
      if (mode === "enquiry") {
        await axios.post(`${API}/enquiries`, {
          name: form.name,
          email: form.email,
          company: form.company || null,
          message: form.message,
          website: form.website || null,
        });
        toast.success(t.contact.toastEnquiry);
      } else {
        await axios.post(`${API}/bookings`, {
          name: form.name,
          email: form.email,
          company: form.company || null,
          preferred_time: form.preferred_time,
          notes: form.message || null,
          website: form.website || null,
        });
        toast.success(t.contact.toastCall);
      }
      setDone(true);
    } catch (err) {
      setError(true);
      toast.error(t.contact.toastError);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-lav py-20 md:py-40">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:grid-cols-12 md:px-10">
        <Reveal className="md:col-span-5">
          <Overline testId="contact-overline">{t.contact.overline}</Overline>
          <h2
            data-testid="contact-heading"
            className="mt-6 font-serif text-4xl font-light leading-tight tracking-tight text-ink md:text-5xl"
          >
            {t.contact.headingA}
            <br />
            <em>{t.contact.headingEm}</em>
          </h2>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-ink/85 md:text-lg">
            {t.contact.para}
          </p>
          <p className="mt-8 text-sm text-ink/75">
            {t.contact.preferEmail}{" "}
            <a
              data-testid="contact-email-link"
              href="mailto:hello@encraft.studio"
              className="font-medium text-ink underline decoration-bee decoration-2 underline-offset-4 transition-colors hover:text-ink/70"
            >
              hello@encraft.studio
            </a>
          </p>
          <p className="mt-4 text-sm text-ink/75">
            {t.contact.orConnect}{" "}
            <a
              data-testid="contact-linkedin-link"
              href="https://www.linkedin.com/in/liisahindikainen/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-ink underline decoration-bee decoration-2 underline-offset-4 transition-colors hover:text-ink/70"
            >
              LinkedIn
            </a>
          </p>
        </Reveal>

        <Reveal delay={0.15} className="md:col-span-6 md:col-start-7">
          <div className="border border-lavline bg-paper p-6 shadow-[0_8px_32px_rgba(37,22,61,0.04)] md:p-12">
            <div
              role="tablist"
              aria-label={t.contact.tablistLabel}
              data-testid="contact-tabs"
              className="mb-10 flex flex-col gap-3 min-[480px]:flex-row"
            >
              {TABS.map((tab, i) => {
                const active = mode === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    id={`contact-tab-${tab.id}`}
                    ref={(el) => (tabRefs.current[i] = el)}
                    aria-selected={active}
                    aria-controls={`contact-panel-${tab.id}`}
                    tabIndex={active ? 0 : -1}
                    onClick={() => selectTab(tab.id)}
                    onKeyDown={(e) => onTabKeyDown(e, i)}
                    data-testid={
                      tab.id === "enquiry" ? "contact-tab-enquiry" : "contact-tab-call"
                    }
                    className={`min-h-[44px] w-full rounded-full px-6 py-3 text-sm font-medium transition-colors duration-300 min-[480px]:w-auto ${
                      active
                        ? "bg-ink text-white"
                        : "border border-ink/25 text-ink/85 hover:border-bee"
                    }`}
                  >
                    <span className="hidden min-[480px]:inline">{tab.label}</span>
                    <span className="min-[480px]:hidden">{tab.short}</span>
                  </button>
                );
              })}
            </div>

            {done ? (
              <div
                data-testid={mode === "enquiry" ? "enquiry-success" : "booking-success"}
                className="flex flex-col items-start gap-6 py-10"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-bee">
                  <Check className="h-6 w-6 text-ink" aria-hidden="true" />
                </span>
                <h3 className="font-serif text-2xl font-normal text-ink md:text-3xl">
                  {mode === "enquiry"
                    ? t.contact.successTitleEnquiry
                    : t.contact.successTitleCall}
                </h3>
                <p className="max-w-md text-base font-light leading-relaxed text-ink/85">
                  {t.contact.successBody}
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another-button"
                  onClick={() => {
                    setDone(false);
                    setForm({
                      name: "",
                      email: "",
                      company: "",
                      message: "",
                      preferred_time: "",
                      website: "",
                    });
                  }}
                  className="text-sm font-medium text-ink underline decoration-bee decoration-2 underline-offset-4"
                >
                  {t.contact.sendAnother}
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit}>
                <div
                  role="tabpanel"
                  id={`contact-panel-${mode}`}
                  aria-labelledby={`contact-tab-${mode}`}
                  className="space-y-8"
                >
                  <p className="text-xs font-medium text-ink/75">
                    <span aria-hidden="true">* </span>
                    {t.contact.requiredNote}
                  </p>
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                    <Field
                      label={t.contact.labels.name}
                      testId={`${mode}-name-input`}
                      value={form.name}
                      onChange={set("name")}
                      placeholder={t.contact.placeholders.name}
                      required
                    />
                    <Field
                      label={t.contact.labels.email}
                      testId={`${mode}-email-input`}
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder={t.contact.placeholders.email}
                      required
                    />
                  </div>
                  <Field
                    label={t.contact.labels.company}
                    testId={`${mode}-company-input`}
                    value={form.company}
                    onChange={set("company")}
                    placeholder={t.contact.placeholders.company}
                  />
                  {mode === "call" && (
                    <Field
                      label={t.contact.labels.time}
                      testId="call-time-input"
                      value={form.preferred_time}
                      onChange={set("preferred_time")}
                      placeholder={t.contact.placeholders.time}
                      required
                    />
                  )}
                  <Field
                    label={
                      mode === "enquiry" ? t.contact.labels.message : t.contact.labels.notes
                    }
                    testId={
                      mode === "enquiry" ? "enquiry-message-input" : "call-notes-input"
                    }
                    as="textarea"
                    value={form.message}
                    onChange={set("message")}
                    placeholder={
                      mode === "enquiry"
                        ? t.contact.placeholders.message
                        : t.contact.placeholders.notes
                    }
                    required={mode === "enquiry"}
                    warning={t.contact.warning}
                  />
                  <div className="hp-field" aria-hidden="true">
                    <label>
                      {t.contact.honeypotLabel}
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={set("website")}
                        data-testid="contact-honeypot-field"
                      />
                    </label>
                  </div>
                </div>
                <div className="mt-10 space-y-5">
                  <p
                    data-testid="privacy-note"
                    className="text-sm font-light leading-relaxed text-ink/85"
                  >
                    {t.contact.privacyPre}
                    <Link
                      to={lang === "fi" ? "/fi/privacy" : "/privacy"}
                      data-testid="privacy-policy-link"
                      className="font-medium text-ink underline decoration-bee decoration-2 underline-offset-4"
                    >
                      {t.contact.privacyLink}
                    </Link>
                    {t.contact.privacyPost}
                  </p>
                  {error && (
                    <p
                      role="alert"
                      data-testid="contact-error"
                      className="border-l-4 border-bee bg-lav px-4 py-3 text-sm font-medium leading-relaxed text-ink"
                    >
                      {t.contact.error}
                    </p>
                  )}
                  <button
                    data-testid={
                      mode === "enquiry" ? "enquiry-submit-button" : "call-submit-button"
                    }
                    type="submit"
                    disabled={sending}
                    className="group flex min-h-[52px] w-full items-center justify-center gap-3 rounded-full bg-ink px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-bee hover:text-ink disabled:opacity-60 sm:w-auto"
                  >
                    {sending
                      ? t.contact.sending
                      : mode === "enquiry"
                        ? t.contact.submitEnquiry
                        : t.contact.submitCall}
                    <ArrowUpRight
                      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                      aria-hidden="true"
                    />
                  </button>
                  <p data-testid="response-time-note" className="text-sm text-ink/75">
                    {t.contact.responseNote}
                  </p>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
